import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FieldInput, invalidateRef } from "@/components/FieldInput";

export const compact = (o) => Object.fromEntries(Object.entries(o).filter(([, v]) => v !== "" && v !== undefined && v !== null));

const groupFields = (fields) => fields.reduce((acc, f) => {
  const s = f.section || "";
  if (!acc.length || acc[acc.length - 1].name !== s) acc.push({ name: s, fields: [] });
  acc[acc.length - 1].fields.push(f);
  return acc;
}, []);

export const FORM_DIALOG = "max-h-[92vh] gap-3 overflow-y-auto p-4 sm:max-w-5xl";

export const FormFields = ({ fields, form, setForm }) => (
  <div className="space-y-3">
    {groupFields(fields).map((g, gi) => (
      <fieldset key={`${g.name}-${gi}`} data-testid={`form-section-${gi}`} className="rounded-md border border-slate-200 bg-slate-50/40 px-3 pb-3 pt-1">
        {g.name && <legend className="px-1.5 text-[10.5px] font-semibold uppercase tracking-wider text-sky-700">{g.name}</legend>}
        <div className="grid grid-cols-2 gap-x-3 gap-y-2 lg:grid-cols-4">
          {g.fields.map((f) => (
            <div key={f.name} className={f.type === "textarea" ? "col-span-2 space-y-1 lg:col-span-4" : "min-w-0 space-y-1"}>
              <Label className="text-[11px] font-medium text-slate-600">{f.label}{f.required && <span className="text-rose-600"> *</span>}</Label>
              <FieldInput field={f} value={form[f.name]} onChange={(v) => setForm((s) => ({ ...s, [f.name]: v }))} />
            </div>
          ))}
        </div>
      </fieldset>
    ))}
  </div>
);

export const RecordDialog = ({ open, onOpenChange, moduleKey, mod, record, onSaved }) => {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(record ? { ...record } : {});
  }, [open, record]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = Object.fromEntries(mod.fields.map((f) => [f.name, form[f.name]]).filter(([, v]) => v !== undefined));
      const { data } = record
        ? await api.put(`/m/${moduleKey}/${record.id}`, payload)
        : await api.post(`/m/${moduleKey}`, payload);
      invalidateRef(moduleKey);
      toast.success(`${data.code} ${record ? "updated" : "created"}`);
      onSaved(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="dialog-record" className={FORM_DIALOG}>
        <DialogHeader>
          <DialogTitle className="font-heading text-base">{record ? `Edit ${record.code}` : `New entry · ${mod.title}`}</DialogTitle>
        </DialogHeader>
        <FormFields fields={mod.fields} form={form} setForm={setForm} />
        <DialogFooter>
          <Button size="sm" variant="outline" onClick={() => onOpenChange(false)} data-testid="btn-cancel-record">Cancel</Button>
          <Button size="sm" onClick={save} disabled={saving} data-testid="btn-save-record">{saving ? "Saving..." : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
